import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyCompletedTasks from '@reader_task_list_view/components/EnhancedTableBodyCompletedTasks'
import { HeadCell } from '@common/components/Table/table.types'
import { ReadTask } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useParams } from 'react-router-dom'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useSearch } from '@common/hooks/useSeach'
import { useReadTasksManagementHandlers } from '@features/reader_task_list_iam_view/pages/hooks/useReadTasksManagementHandlers'
import { Stack, Typography, TableBody, TableRow, TableCell } from '@mui/material'
import { SPACING } from '@common/theme/spacing'

interface ReaderTaskListTableCompletedTasksProps {
  subjectId?: string
}

const headCells: readonly HeadCell<ReadTask>[] = [
  { id: 'task_id', disablePadding: true, label: 'Task pseudo ID', disableSort: false },
  { id: 'blind_code', disablePadding: false, label: 'Blind code', disableSort: false },
  { id: 'timepoint_pseudo_name', disablePadding: false, label: 'Visit', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Type', disableSort: false },
  { id: 'priority', disablePadding: false, label: 'Priority', disableSort: false },
  { id: 'created_at', disablePadding: false, label: 'Created at', disableSort: false },
  { id: 'completed_date', disablePadding: false, label: 'Completed at', disableSort: false },
  { id: 'due_date', disablePadding: false, label: 'Time spent', disableSort: false },
  { id: 'status', disablePadding: false, label: 'Status', disableSort: false },
]

function EmptyCompletedReaderTasksTable({ searchValue }: { searchValue?: string }) {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">
        {searchValue ? 'No matching completed tasks found' : 'No completed reader tasks available'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {searchValue
          ? 'Try adjusting your search criteria or clear the search field'
          : 'There are currently no completed reader tasks to display'}
      </Typography>
    </Stack>
  )
}

export default function ReaderTaskListTableCompletedTasks({ subjectId }: ReaderTaskListTableCompletedTasksProps) {
  const { studyId } = useParams()
  const { value } = useSearch()
  const { user } = useExtendedAuth()
  const {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useReadTasksManagementHandlers({
    category: 'complete_tasks',
    studyId: studyId!,
    subjectId,
    searchValue: value,
  })

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasCompletedTasks = data?.data?.items && data.data.items.length > 0

  return (
    <EnhancedTable<ReadTask>
      rows={data?.data?.items || []}
      page={page}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof ReadTask}
      rowsPerPage={paginationParams.limit || 0}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      next_cursor={data?.data?.pagination.next_cursor}
      previous_cursor={data?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasCompletedTasks ? (
        <EnhancedTableBodyCompletedTasks
          rows={data?.data?.items || []}
          order={order}
          orderBy={orderBy}
          authedUserToken={user?.access_token}
          authedUserId={user?.profile.sub}
        />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyCompletedReaderTasksTable searchValue={value} />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
